import { Icon } from './icons.jsx';

/**
 * Reusable KPI tile. Replaces ~40 lines of inline HTML per tile in
 * the legacy console with a single component call.
 *
 *   <KpiTile
 *     label="Total users"
 *     iconName="users"
 *     value={42}
 *     valueFormatter={(v) => v.toLocaleString()}
 *     delta={{ pct: 12.4, post: 'vs prior 30d', direction: 'up' }}
 *     iconColor="blue"        // 'blue' | 'orange' | 'green' | default purple
 *     loading={false}
 *   />
 *
 * Props:
 *   label      — top-row caption (Geist Mono uppercase)
 *   iconName   — icon set key (passed through to <Icon>)
 *   value      — main number; rendered raw unless valueFormatter given
 *   sub        — optional <small> suffix next to the value
 *   delta      — { pct?, raw?, post?, direction? 'up'|'down' } — pct
 *                 wins if both pct + raw set
 *   iconColor  — colour class for the glyph chip
 *   loading    — when true, show "—" everywhere
 *
 * Keep this component dumb (no fetch, no context) so it stays
 * trivially composable in any page.
 *
 * @author <lukman@zshift.io>
 */
export function KpiTile({
  label,
  iconName,
  value,
  sub,
  delta,
  iconColor,
  valueFormatter,
  loading,
}) {
  const shown = loading || value == null
    ? '—'
    : (valueFormatter ? valueFormatter(value) : value);

  return (
    <div className="kpi">
      <div className={`label ${iconColor || ''}`}>
        <span className="ic"><Icon name={iconName} size={13} /></span>
        {label}
      </div>
      <div className="value">
        {shown}
        {sub && !loading && <small>{sub}</small>}
      </div>
      {delta && !loading && (
        <div className={`delta ${delta.direction === 'down' ? 'down' : ''}`}>
          {delta.direction === 'up'   && <Icon name="arrowUp"   size={11} />}
          {delta.direction === 'down' && <Icon name="arrowDown" size={11} />}
          {delta.pct != null && (
            <>
              {delta.pct >= 0 ? '+' : ''}{delta.pct.toFixed(1)}%
            </>
          )}
          {delta.pct == null && delta.raw != null && delta.raw}
          {delta.post && <span className="post">{delta.post}</span>}
        </div>
      )}
    </div>
  );
}
